import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Message, Spin } from '@arco-design/web-react';
import { hasAuthMarkerCookie } from '@/utils/authSession';
import checkLogin from '@/utils/checkLogin';
import { resolveLandingPath } from '@/routes';
import styles from './style.module.less';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(hasAuthMarkerCookie());

  useEffect(() => {
    document.title = `${t('auth.login.title')} - LetsFly`;
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      if (!hasAuthMarkerCookie()) {
        setChecking(false);
        return;
      }

      const user = await checkLogin(dispatch);
      if (cancelled) {
        return;
      }

      if (user) {
        const from = (location.state as { from?: string } | null)?.from;
        const target =
          from && from !== '/login' && from !== '/auth/callback'
            ? from
            : resolveLandingPath(user.permissions ?? []);
        navigate(target, { replace: true });
        return;
      }

      setChecking(false);
    }

    void hydrate();

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.state, navigate]);

  useEffect(() => {
    const error = searchParams.get('error');
    if (!error) {
      return;
    }

    const errorKeys: Record<string, string> = {
      invalid_state: 'auth.login.error.invalid_state',
      missing_code: 'auth.login.error.missing_code',
      access_denied: 'auth.login.error.access_denied',
      email_conflict: 'auth.login.error.email_conflict',
      bootstrap_failed: 'auth.login.error.bootstrap_failed',
      default_role_missing: 'auth.login.error.default_role_missing',
      oauth_failed: 'auth.login.error.oauth_failed',
    };

    // 新注册用户禁用属正常流程，以成功提示展示注册结果，不走红色错误 toast
    if (error === 'account_disabled') {
      Message.success(t('auth.login.error.account_disabled'));
      return;
    }

    Message.error(t(errorKeys[error] || 'auth.login.error.default'));
  }, [searchParams, t]);

  const handleFeishuLogin = () => {
    // 走当前源 /api，经 setupProxy 转发，保证 Session/CSRF Cookie 同源
    window.location.href = '/api/auth/feishu/redirect';
  };

  if (checking) {
    return (
      <div className={styles.container}>
        <Spin size={32} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('auth.login.title')}</h1>
        <p className={styles.subtitle}>LetsFly 管理后台</p>
        <Button type="primary" long size="large" onClick={handleFeishuLogin}>
          {t('auth.login.feishu')}
        </Button>
      </div>
    </div>
  );
}
